import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  AssistantLinkTarget,
  ProposeActionConfirm,
  ProposeActionDisplay,
} from '@core/tellent-assistant-types';
import { ButtonComponent, IconComponent, type IconName } from '@core/user-interface-legacy';

import { TellentAssistantLinkDirective } from '../../link/assistant-link.directive';

/** A resolved link to the resource the executed action created/affected. */
export interface ProposalResultLink {
  label: string;
  url: string;
  target?: AssistantLinkTarget;
}

const VALUE_LIMIT = 280;

function humanizeKey(key: string): string {
  const words = key.replace(/_(html|json)$/, '').replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function displayValue(value: unknown): string {
  const text =
    typeof value === 'string' ? value.replace(/<[^>]+>/g, ' ') : (JSON.stringify(value) ?? '');
  const collapsed = text.replace(/\s+/g, ' ').trim();
  return collapsed.length > VALUE_LIMIT ? `${collapsed.slice(0, VALUE_LIMIT)}…` : collapsed;
}

/**
 * A proposed action rendered inline in the assistant message: a preview of
 * WHAT will happen, with the decision (skip / confirm) built into the card
 * footer. Once confirmed and executed, the footer collapses into a muted
 * approved row and a success banner appears under the card, linking to the
 * created resource when the action declared one.
 */
@Component({
  selector: 'tellent-assistant-proposal-preview',
  imports: [ButtonComponent, IconComponent, TellentAssistantLinkDirective, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="card">
      <header class="header">
        <rt-icon class="header-icon" [name]="icon()" />
        <span class="header-label">{{ label() }}</span>
      </header>
      <div class="content">
        @if (bodyText()) {
          <p class="body">{{ bodyText() }}</p>
        } @else {
          @if (summaryText()) {
            <p class="body">{{ summaryText() }}</p>
          }
          @if (properties().length > 0) {
            <dl class="properties">
              @for (property of properties(); track property.label) {
                <div class="property">
                  <dt class="property-label">{{ property.label }}</dt>
                  <dd class="property-value">{{ property.value }}</dd>
                </div>
              }
            </dl>
          }
        }
      </div>

      @if (auto()) {
        <footer class="outcome">
          <rt-icon class="outcome-icon" name="check" />
          <span>{{ doneText() }}</span>
          <span class="outcome-hint">
            · {{ 'tlc.tellent_assistant.proposal.automatic' | translate }}
          </span>
        </footer>
      } @else {
        <footer class="decision">
          @switch (resolvedStatus()) {
            @case ('confirmed') {
              <div class="resolved">
                <rt-icon class="resolved-icon" name="check" />
                <span>{{ approvedText() }}</span>
              </div>
            }
            @case ('cancelled') {
              <div class="resolved">
                <span>{{ 'tlc.tellent_assistant.proposal.skipped' | translate }}</span>
              </div>
            }
            @default {
              <h4 class="question">{{ question() }}</h4>
              @if (subtitle()) {
                <p class="subtitle">{{ subtitle() }}</p>
              }
              @if (errored()) {
                <p class="error">{{ 'tlc.tellent_assistant.proposal.error' | translate }}</p>
              }
              <div class="actions">
                <rt-button variant="plain" [disabled]="busy()" (click)="skipped.emit()">
                  {{ 'tlc.tellent_assistant.proposal.skip' | translate }}
                </rt-button>
                <rt-button variant="primary" [disabled]="busy()" (click)="confirmed.emit()">
                  {{ yesLabel() }}
                </rt-button>
              </div>
            }
          }
        </footer>
      }
    </section>

    @if (!auto() && resolvedStatus() === 'confirmed') {
      <div class="success-banner">
        <rt-icon class="banner-icon" name="check" />
        <span class="banner-text">{{ doneText() }}</span>
        @if (resultLink(); as link) {
          @if (link.target; as linkTarget) {
            <a
              class="banner-link"
              [tellentAssistantLink]="linkTarget"
              target="_blank"
              rel="noopener noreferrer"
              [attr.aria-label]="link.label"
            >
              <rt-icon name="open-in-new" />
            </a>
          } @else {
            <a
              class="banner-link"
              [href]="link.url"
              target="_blank"
              rel="noopener noreferrer"
              [attr.aria-label]="link.label"
            >
              <rt-icon name="open-in-new" />
            </a>
          }
        }
      </div>
    }
  `,
  styleUrl: './proposal-preview.component.less',
})
export class TellentAssistantProposalPreviewComponent {
  private readonly i18n = inject(I18nService);

  private readonly argsRecord = computed<Record<string, unknown>>(() => {
    const value = this.args();
    return typeof value === 'object' && value !== null ? <Record<string, unknown>>value : {};
  });

  protected readonly label = computed(
    () => this.confirm()?.preview_label ?? this.confirm()?.title ?? '',
  );

  // The contract carries a plain string; unknown names fail soft (no glyph).
  protected readonly icon = computed(() => <IconName>(this.confirm()?.icon ?? 'description'));

  protected readonly bodyText = computed(() => {
    const body = this.argsRecord()['body'];
    return typeof body === 'string' ? body : '';
  });

  protected readonly summaryText = computed(() => this.display()?.summary ?? '');

  // Prefer the model-authored properties (resolved names, never raw ids);
  // fall back to the raw args with id-ish keys hidden — an id means nothing
  // to the user, and the summary line carries the referenced record's name.
  protected readonly properties = computed(() => {
    const authored = this.display()?.properties;
    if (authored?.length) return authored;
    return Object.entries(this.argsRecord())
      .filter(
        ([key, value]) =>
          key !== 'body' &&
          !key.endsWith('_id') &&
          !key.endsWith('_ids') &&
          value != null &&
          value !== '',
      )
      .map(([key, value]) => ({ label: humanizeKey(key), value: displayValue(value) }));
  });

  protected readonly question = computed(
    () =>
      this.confirm()?.question ??
      this.i18n.translate('tlc.tellent_assistant.proposal.confirm_fallback'),
  );

  protected readonly subtitle = computed(
    () => this.confirm()?.summary ?? this.confirm()?.effect ?? '',
  );

  protected readonly yesLabel = computed(
    () =>
      this.confirm()?.yes_label ?? this.i18n.translate('tlc.tellent_assistant.proposal.confirm'),
  );

  protected readonly approvedText = computed(
    () =>
      this.confirm()?.approved_label ??
      this.i18n.translate('tlc.tellent_assistant.proposal.approved'),
  );

  protected readonly doneText = computed(
    () =>
      this.display()?.done ??
      this.confirm()?.done_label ??
      this.i18n.translate('tlc.tellent_assistant.proposal.done_fallback'),
  );

  public readonly args = input<unknown>();
  /** Model-authored presentation of the proposal in user terms (names, not ids). */
  public readonly display = input<ProposeActionDisplay | null>(null);
  public readonly confirm = input<ProposeActionConfirm | null>(null);
  /** Link to the created/affected resource; present once the action executed. */
  public readonly resultLink = input<ProposalResultLink | null>(null);
  /** How this proposal was resolved; `null` while the decision is pending. */
  public readonly resolvedStatus = input<'confirmed' | 'cancelled' | null>(null);
  /** True for auto-policy actions the agent already executed (receipt mode). */
  public readonly auto = input<boolean>(false);
  /** True while this proposal's confirmation is being dispatched. */
  public readonly busy = input<boolean>(false);
  /** True when the last confirmation attempt failed (the card stays actionable). */
  public readonly errored = input<boolean>(false);

  public readonly confirmed = output<void>();
  public readonly skipped = output<void>();
}
